import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Flag, Ban } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, Loading, ErrorState, Empty } from "@/components/States";
import { TrustSummary } from "@/components/TrustSummary";
import { ListingCard } from "@/components/ListingCard";
import { ReportDialog } from "@/components/DealDialogs";
import { card, btnGhost } from "@/lib/ui";

export default function PublicProfile() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [report, setReport] = useState(false);
  const load = useCallback(() => {
    api.get(`/users/${id}/profile`).then(({ data }) => setP(data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  const blocked = user?.blocked?.includes(id);
  const toggleBlock = async () => {
    try {
      if (blocked) await api.delete(`/users/${id}/block`);
      else await api.post(`/users/${id}/block`);
      await refresh();
      toast.success(blocked ? "Unblocked" : "Blocked");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!p) return <Loading />;
  return (
    <div className="space-y-6">
      <div className={card + " max-w-3xl"}>
        <div className="flex items-center gap-4 mb-4">
          <Avatar user={p.user} size={72} />
          <div>
            <h1 data-testid="public-profile-name" className="text-2xl font-extrabold text-slate-900">{p.user.first_name}</h1>
            <p className="text-sm text-slate-500">{p.user.city} · Joined {fmtDate(p.user.created_at)}</p>
            {p.user.is_demo && <span className="text-[11px] font-bold uppercase text-slate-500">Demo account · fictional</span>}
          </div>
        </div>
        <TrustSummary profile={p} />
        {user && user.id !== id && (
          <div className="flex gap-2 mt-4">
            <button data-testid="profile-report-btn" onClick={() => setReport(true)} className={btnGhost}><Flag className="h-4 w-4" /> Report</button>
            <button data-testid="profile-block-btn" onClick={toggleBlock} className={btnGhost}><Ban className="h-4 w-4" /> {blocked ? "Unblock" : "Block"}</button>
          </div>
        )}
      </div>
      {p.ratings.reviews.length > 0 && (
        <div className="max-w-3xl space-y-2">
          <h2 className="font-bold text-slate-900">Ratings from confirmed transactions</h2>
          {p.ratings.reviews.map((r, i) => (
            <div key={i} data-testid={`review-${i}`} className={card}>
              <div className="flex items-center gap-1 mb-1">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}<span className="text-xs text-slate-500 ml-2">{r.reviewer?.first_name} · as {r.reviewer_role} · {fmtDate(r.created_at)}</span></div>
              {r.comment && <p className="text-sm text-slate-700">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
      <div>
        <h2 className="font-bold text-slate-900 mb-3">Active listings ({p.active_listings.length})</h2>
        {p.active_listings.length === 0 ? <Empty title="No active listings" /> : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">{p.active_listings.map((l) => <ListingCard key={l.id} item={l} />)}</div>
        )}
      </div>
      <ReportDialog open={report} onOpenChange={setReport} targetType="user" targetId={id} />
    </div>
  );
}
